/**
 * Download a PromptPay QR as a PNG the customer can open on another device.
 *
 * Prefers a composed card (navy frame, PromptPay badge, QR, payee/amount
 * footer — the same layout QRCard.tsx renders on screen, matching the Thai
 * Bankers' Association's own recommended static-QR display convention) so a
 * photo-gallery scan still carries context. Remote QR hosts often lack CORS
 * for canvas export, so we fetch into a same-origin blob URL first when
 * possible, then fall back to the raw image bytes (or a plain download link).
 *
 * Deliberately does NOT reproduce the "Thai QR Payment" association's own
 * logo/wordmark — that is their institutional branding, not the PromptPay
 * payment-method badge (assets/brand/promptpay-logo.png, the real logo,
 * user-supplied) this draws instead.
 */

import promptPayLogo from '../assets/brand/promptpay-logo.png';

export type QrDownloadMeta = {
  /** Already-formatted amount line, e.g. "฿300.00". */
  amountLine: string;
  /** Already-formatted expiry line, or null when the top-up has no expiry. */
  expiresLine: string | null;
  /** Suggested file name ending in .png. */
  fileName: string;
  /** Small subtitle drawn above the card, e.g. "IC Lueur Ai · PromptPay". */
  title: string;
  /** The payee/account name to show in the card's footer, or null to omit it (matches company_qr_payee_name being unset). */
  payeeName: string | null;
  /**
   * True (default) draws the navy-framed card with the PromptPay badge —
   * for the LLMR-122 company QR, a bare EMVCo image we generate ourselves.
   * False draws just the image plus a small amount/expiry caption — for
   * the Omise-issued QR, which already comes fully branded in its own
   * pixels; framing it again doubled up visually (reported 2026-09-01).
   */
  framed?: boolean;
};

function triggerBlobDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    window.setTimeout(() => URL.revokeObjectURL(url), 2_000);
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // Only needed when drawing a remote URL directly onto a canvas.
    if (/^https?:/i.test(src)) img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('qr_image_load_failed'));
    img.src = src;
  });
}

const navy = '#0b3d7a';

/** Rounded-rect path via arcTo — broader support than ctx.roundRect. */
function roundedRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** Draws the real PromptPay logo image centered at (cx, y), scaled to targetW wide, aspect preserved. Returns the drawn height. */
function drawBadgeImage(ctx: CanvasRenderingContext2D, badge: HTMLImageElement, cx: number, y: number, targetW: number): number {
  const srcW = badge.naturalWidth || badge.width;
  const srcH = badge.naturalHeight || badge.height;
  const h = targetW * (srcH / srcW);
  ctx.drawImage(badge, cx - targetW / 2, y, targetW, h);
  return h;
}

/**
 * Composes the same navy-framed card QRCard.tsx renders on screen: an outer
 * border, the PromptPay badge, the QR itself, a divider, and a footer row
 * with the payee name on the left and the amount (bold, prominent) on the
 * right — the Thai Bankers' Association's own recommended layout for a
 * static PromptPay QR, minus their own institutional logo (see this file's
 * top-level doc comment for why that specific mark is not reproduced here).
 */
function composeCard(img: HTMLImageElement, badge: HTMLImageElement, meta: QrDownloadMeta): HTMLCanvasElement {
  const outerBorder = 4;
  const pad = 24;
  const qrSize = 300;
  const gap = 16;
  const titleH = meta.title ? 20 : 0;
  const badgeTargetW = 130;
  const badgeH = badgeTargetW * ((badge.naturalHeight || badge.height) / (badge.naturalWidth || badge.width));
  const footerH = meta.payeeName ? 40 : 30;

  const contentW = qrSize;
  const width = contentW + pad * 2 + outerBorder * 2;
  const height =
    outerBorder * 2 +
    pad +
    (titleH ? titleH + gap : 0) +
    badgeH +
    gap +
    qrSize +
    gap +
    footerH +
    (meta.expiresLine ? 22 : 0) +
    pad;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas_unavailable');

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);

  // Outer navy frame, matching the reference's blue border.
  ctx.strokeStyle = navy;
  ctx.lineWidth = outerBorder;
  roundedRectPath(ctx, outerBorder / 2, outerBorder / 2, width - outerBorder, height - outerBorder, 14);
  ctx.stroke();

  const cx = width / 2;
  let y = outerBorder + pad;

  if (meta.title) {
    ctx.fillStyle = '#777777';
    ctx.font = '400 13px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(meta.title, cx, y, contentW);
    y += titleH + gap;
  }

  drawBadgeImage(ctx, badge, cx, y, badgeTargetW);
  y += badgeH + gap;

  const srcW = img.naturalWidth || img.width;
  const srcH = img.naturalHeight || img.height;
  const scale = Math.min(qrSize / srcW, qrSize / srcH);
  const drawW = srcW * scale;
  const drawH = srcH * scale;
  const dx = cx - drawW / 2;
  ctx.drawImage(img, dx, y, drawW, drawH);
  y += qrSize + gap;

  // Divider above the footer, same visual break the reference draws between
  // the QR and the account-name/amount line.
  ctx.strokeStyle = '#e0e0e0';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(outerBorder + pad, y);
  ctx.lineTo(width - outerBorder - pad, y);
  ctx.stroke();
  y += 10;

  const footerLeft = outerBorder + pad;
  const footerRight = width - outerBorder - pad;
  if (meta.payeeName) {
    ctx.fillStyle = '#555555';
    ctx.font = '400 13px system-ui, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(meta.payeeName, footerLeft, y, contentW * 0.55);
  }
  ctx.fillStyle = navy;
  ctx.font = '700 24px system-ui, sans-serif';
  ctx.textAlign = 'right';
  ctx.textBaseline = 'top';
  ctx.fillText(meta.amountLine, footerRight, y);
  y += footerH;

  if (meta.expiresLine) {
    ctx.fillStyle = '#888888';
    ctx.font = '400 13px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(meta.expiresLine, cx, y, contentW);
  }

  return canvas;
}

/**
 * Composes just the QR image plus a small centered amount/expiry caption —
 * no frame, no badge. Mirrors QRCard.tsx's `framed={false}` on-screen
 * layout, for the Omise-issued QR (see composeCard's own doc comment for
 * why that one gets no frame here).
 */
function composePlain(img: HTMLImageElement, meta: QrDownloadMeta): HTMLCanvasElement {
  const pad = 12;
  const qrSize = 300;
  const gap = 10;
  const captionH = 20;

  const width = qrSize;
  const height = qrSize + gap + captionH + pad;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas_unavailable');

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);

  const srcW = img.naturalWidth || img.width;
  const srcH = img.naturalHeight || img.height;
  const scale = Math.min(qrSize / srcW, qrSize / srcH);
  const drawW = srcW * scale;
  const drawH = srcH * scale;
  ctx.drawImage(img, (width - drawW) / 2, 0, drawW, drawH);

  const caption = meta.expiresLine ? `${meta.amountLine} · ${meta.expiresLine}` : meta.amountLine;
  ctx.fillStyle = '#555555';
  ctx.font = '400 13px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillText(caption, width / 2, qrSize + gap, width);

  return canvas;
}

function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    try {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('png_encode_failed'));
      }, 'image/png');
    } catch (err) {
      reject(err instanceof Error ? err : new Error('png_encode_failed'));
    }
  });
}

function guessExt(mime: string): string {
  if (mime.includes('jpeg') || mime.includes('jpg')) return '.jpg';
  if (mime.includes('webp')) return '.webp';
  if (mime.includes('svg')) return '.svg';
  return '.png';
}

async function fetchAsObjectUrl(src: string): Promise<{ url: string; blob: Blob } | null> {
  if (!/^https?:/i.test(src)) return null;
  try {
    const res = await fetch(src, { mode: 'cors' });
    if (!res.ok) return null;
    const blob = await res.blob();
    return { url: URL.createObjectURL(blob), blob };
  } catch {
    return null;
  }
}

function triggerAnchorDownload(href: string, fileName: string): void {
  const a = document.createElement('a');
  a.href = href;
  a.download = fileName;
  a.target = '_blank';
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// The badge is a bundled local asset — identical on every call, so it is
// loaded once and reused rather than re-decoded per download.
let badgeImagePromise: Promise<HTMLImageElement> | null = null;
function loadBadgeImage(): Promise<HTMLImageElement> {
  if (!badgeImagePromise) badgeImagePromise = loadImage(promptPayLogo);
  return badgeImagePromise;
}

/** Save `src` (QR image URL or data URL) as a downloadable image file. */
export async function downloadQrImage(src: string, meta: QrDownloadMeta): Promise<void> {
  const fetched = await fetchAsObjectUrl(src);
  const imageSrc = fetched?.url ?? src;

  try {
    const framed = meta.framed ?? true;
    let canvas: HTMLCanvasElement;
    if (framed) {
      const [img, badge] = await Promise.all([loadImage(imageSrc), loadBadgeImage()]);
      canvas = composeCard(img, badge, meta);
    } else {
      canvas = composePlain(await loadImage(imageSrc), meta);
    }
    const blob = await canvasToPngBlob(canvas);
    triggerBlobDownload(blob, meta.fileName);
    return;
  } catch {
    // Compose failed (tainted canvas / load). Prefer the already-fetched blob.
    if (fetched) {
      const named =
        fetched.blob.type.startsWith('image/') && !fetched.blob.type.includes('png')
          ? meta.fileName.replace(/\.png$/i, guessExt(fetched.blob.type))
          : meta.fileName;
      triggerBlobDownload(fetched.blob, named);
      return;
    }
    triggerAnchorDownload(src, meta.fileName);
  } finally {
    if (fetched) URL.revokeObjectURL(fetched.url);
  }
}
